import { createClient } from '@/util/supabase/component'
import type { BulkSpeciesLookupResponse } from '@/types'
import { booleanFilterTypeGuard } from '@/util/helpers'

export interface SpeciesValidationResult {
  speciesLookupMap: BulkSpeciesLookupResponse
  errors: Record<string, string>
}

export class SpeciesValidationService {
  private readonly supabase = createClient()

  validateTaxonName(taxonName: string): { isValid: boolean; error?: string } {
    const strings = taxonName
      .split(' ')
      .map((s) => s.trim())
      .filter(booleanFilterTypeGuard)

    if (strings.length < 2) {
      return {
        isValid: false,
        error: `Species lookup failed for ${taxonName}: Taxon name must have at least two words`,
      }
    }

    return { isValid: true }
  }

  async lookupSpecies(taxonNames: string[]): Promise<SpeciesValidationResult> {
    const errors: Record<string, string> = {}

    // Validate each taxon name first
    const validTaxonNames: string[] = []
    taxonNames.forEach((taxonName, index) => {
      const validation = this.validateTaxonName(taxonName)
      if (validation.isValid) {
        validTaxonNames.push(taxonName)
      } else if (validation.error) {
        errors[index] = validation.error
      }
    })

    if (validTaxonNames.length === 0) {
      return { speciesLookupMap: {}, errors }
    }

    // Lookup species in bulk
    const uniqueTaxonNames = Array.from(new Set(validTaxonNames))
    const { data, error } =
      await this.supabase.functions.invoke<BulkSpeciesLookupResponse>(
        'bulk_species_lookup',
        {
          body: { species: uniqueTaxonNames },
        },
      )

    if (error) {
      throw new Error((error as Error).message)
    }

    if (!data) {
      throw new Error('Unable to lookup species names')
    }

    return {
      speciesLookupMap: data,
      errors,
    }
  }
}
